import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import {
  Plus, Database, History, RotateCcw, Loader2,
  RefreshCw, Save, Key, LinkIcon, Columns, ArrowRight,
  ChevronUp, ChevronDown, Edit2, Eye, X, Tag, FileText, Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  getAllMetadata, getAllMetadataVersions, updateColumnMetadata,
  addColumnMetadata, addLink,
  restoreMetadataVersion,
  regenerateGlossary, getConcepts,
  getUsecaseSummary, generateUsecaseSummary, updateUsecaseSummary,
  getUsecaseSummaryVersions, getUsecaseSummaryDiff, restoreUsecaseSummaryVersion
} from '../lib/api';

// Column definitions for Column Metadata table (removed concept_name)
const COLUMN_METADATA_COLUMNS = [
  { key: 'source_table', label: 'Source Table', width: 120, editable: false, type: 'text' },
  { key: 'source_column', label: 'Source Column', width: 130, editable: false, type: 'text' },
  { key: 'row_count_system', label: 'Row Count', width: 100, editable: false, type: 'number' },
  { key: 'unique_count_system', label: 'Unique Count', width: 100, editable: false, type: 'number' },
  { key: 'uniqueness_pct_system', label: 'Uniqueness %', width: 100, editable: false, type: 'percent' },
  { key: 'completeness_pct_system', label: 'Completeness %', width: 110, editable: false, type: 'percent' },
  { key: 'table_alias', label: 'Table Alias', width: 100, editable: true, type: 'text', editGroup: 'names' },
  { key: 'column_alias', label: 'Column Alias', width: 120, editable: true, type: 'text', editGroup: 'names' },
  { key: 'datatype_l1', label: 'Datatype L1', width: 100, editable: true, type: 'text', editGroup: 'names' },
  { key: 'datatype_l2', label: 'Datatype L2', width: 120, editable: true, type: 'text', editGroup: 'names' },
  { key: 'is_pii', label: 'PII', width: 60, editable: true, type: 'boolean' },
  { key: 'is_ignored', label: 'Ignored', width: 70, editable: true, type: 'boolean' },
  { key: 'column_tags', label: 'Column Tags', width: 150, editable: true, type: 'tags' },
  { key: 'column_glossary', label: 'Column Glossary', width: 200, editable: true, type: 'text', editGroup: 'glossary' },
  { key: 'table_glossary', label: 'Table Glossary', width: 200, editable: true, type: 'text', editGroup: 'glossary' },
  { key: 'table_type', label: 'Table Type', width: 100, editable: true, type: 'text', editGroup: 'names' },
  { key: 'column_type', label: 'Column Type', width: 100, editable: true, type: 'text', editGroup: 'names' },
  { key: 'is_primary_key', label: 'PK', width: 50, editable: true, type: 'boolean' },
  { key: 'is_foreign_key', label: 'FK', width: 50, editable: true, type: 'boolean' },
  { key: 'calculation_logic', label: 'Calculation Logic', width: 180, editable: true, type: 'text', editGroup: 'names' },
  { key: 'row_shortlisting_enabled', label: 'Row Shortlist', width: 100, editable: true, type: 'boolean' },
  { key: 'auto_correction_enabled', label: 'Auto Correct', width: 100, editable: true, type: 'boolean' },
  { key: 'sample_values', label: 'Samples', width: 80, editable: false, type: 'samples' },
];

// Column definitions for Link Identification table (removed concept_name)
const LINK_COLUMNS = [
  { key: 'parent_table_name', label: 'Parent Table', width: 130, editable: false, type: 'text' },
  { key: 'parent_column_name', label: 'Parent Column', width: 130, editable: false, type: 'text' },
  { key: 'child_table_name', label: 'Child Table', width: 130, editable: false, type: 'text' },
  { key: 'child_column_name', label: 'Child Column', width: 130, editable: false, type: 'text' },
  { key: 'mapped_unique_value_count', label: 'Mapped Unique', width: 120, editable: false, type: 'number' },
  { key: 'parent_row_count_mapped', label: 'Parent Rows', width: 110, editable: false, type: 'number' },
  { key: 'parent_unique_value_count_mapped', label: 'Parent Unique', width: 120, editable: false, type: 'number' },
  { key: 'child_row_count_mapped', label: 'Child Rows', width: 110, editable: false, type: 'number' },
  { key: 'child_unique_value_count_mapped', label: 'Child Unique', width: 120, editable: false, type: 'number' },
];

// Cell renderer component
function TableCell({ value, type, editable, onEdit, onViewSamples, onEditTags }) {
  // Boolean toggle
  if (type === 'boolean') {
    return (
      <div className="flex items-center justify-center h-full">
        <Switch
          checked={value || false}
          onCheckedChange={onEdit}
          disabled={!editable}
          className="data-[state=checked]:bg-[#0EA5E9]"
        />
      </div>
    );
  }

  // Number display
  if (type === 'number') {
    return (
      <span className="font-mono text-xs text-[#94A3B8]">
        {value != null ? value.toLocaleString() : '-'}
      </span>
    );
  }

  // Percent display
  if (type === 'percent') {
    return (
      <span className="font-mono text-xs text-[#94A3B8]">
        {value != null ? `${value.toFixed(1)}%` : '-'}
      </span>
    );
  }

  // Sample values - show eye icon
  if (type === 'samples') {
    const items = Array.isArray(value) ? value : [];
    return (
      <div className="flex items-center justify-center">
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={onViewSamples}
          title={items.length > 0 ? `${items.length} sample values` : 'No samples'}
        >
          <Eye className={`w-4 h-4 ${items.length > 0 ? 'text-[#0EA5E9]' : 'text-[#64748B]'}`} />
        </Button>
      </div>
    );
  }

  // Tags - editable with edit icon
  if (type === 'tags') {
    const items = Array.isArray(value) ? value : [];
    return (
      <div 
        className="group flex items-center gap-1 cursor-pointer hover:bg-[#1E293B] px-1 py-0.5 rounded min-h-[20px] w-full"
        onClick={onEditTags}
      >
        <div className="flex flex-wrap gap-1 flex-1 overflow-hidden">
          {items.slice(0, 2).map((item, i) => (
            <Badge key={i} variant="outline" className="text-[9px] px-1 py-0">{item}</Badge>
          ))}
          {items.length > 2 && <span className="text-[9px] text-[#64748B]">+{items.length - 2}</span>}
          {items.length === 0 && <span className="text-[#64748B] italic text-xs">-</span>}
        </div>
        <Tag className="w-3 h-3 text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }

  // Text - editable shows edit icon on hover
  if (editable) {
    return (
      <div 
        className="group flex items-center gap-1 cursor-pointer hover:bg-[#1E293B] px-1 py-0.5 rounded min-h-[20px] w-full"
        onClick={onEdit}
      >
        <span className="text-xs text-[#F1F5F9] truncate flex-1" title={value}>
          {value || <span className="text-[#64748B] italic">-</span>}
        </span>
        <Edit2 className="w-3 h-3 text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }

  // Non-editable text
  return (
    <span className="text-xs text-[#F1F5F9] truncate" title={value}>
      {value || '-'}
    </span>
  );
}

// Excel-like Table Component
function ExcelTable({ columns, data, onToggle, onEditCell, onViewSamples, onEditTags }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);

  return (
    <div className="flex flex-col h-full border border-[#1E293B] rounded-md overflow-hidden">
      <div className="flex-1 overflow-auto">
        <table className="border-collapse" style={{ minWidth: totalWidth }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#0F172A]">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="border-b border-r border-[#1E293B] px-2 py-2 text-left cursor-pointer hover:bg-[#1E293B] select-none"
                  style={{ width: col.width, minWidth: col.width }}
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wide truncate">
                      {col.label}
                    </span>
                    {sortColumn === col.key && (
                      sortDirection === 'asc' 
                        ? <ChevronUp className="w-3 h-3 text-[#0EA5E9]" />
                        : <ChevronDown className="w-3 h-3 text-[#0EA5E9]" />
                    )}
                    {!col.editable && col.type !== 'boolean' && col.type !== 'samples' && (
                      <span className="text-[8px] text-[#64748B]">🔒</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={`
                  ${rowIndex % 2 === 0 ? 'bg-[#030712]' : 'bg-[#0F172A]/30'}
                  hover:bg-[#1E293B]/50 transition-colors
                `}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="border-b border-r border-[#1E293B] px-2 py-1"
                    style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                  >
                    <TableCell
                      value={row[col.key]}
                      type={col.type}
                      editable={col.editable && col.type !== 'boolean' && col.type !== 'tags'}
                      onEdit={
                        col.type === 'boolean' 
                          ? (val) => onToggle(row, col.key, val)
                          : col.editable ? () => onEditCell(row, col) : undefined
                      }
                      onViewSamples={col.type === 'samples' ? () => onViewSamples(row) : undefined}
                      onEditTags={col.type === 'tags' ? () => onEditTags(row) : undefined}
                    />
                  </td>
                ))}
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center py-8 text-[#64748B]">
                  No data available. Click "Add" to create a new row.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-[#0F172A] border-t border-[#1E293B] px-3 py-1 flex items-center justify-between text-xs text-[#64748B]">
        <span>{data.length} row{data.length !== 1 ? 's' : ''}</span>
        <span>{columns.filter(c => c.editable).length} editable columns</span>
      </div>
    </div>
  );
}

export default function Metadata() {
  const [allColumns, setAllColumns] = useState([]);
  const [allLinks, setAllLinks] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('columns');
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Tags edit dialog
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  const [tagsRow, setTagsRow] = useState(null);
  const [currentTags, setCurrentTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  
  // Sample values dialog
  const [showSamplesDialog, setShowSamplesDialog] = useState(false);
  const [samplesRow, setSamplesRow] = useState(null);
  
  // Version history dialog
  const [showVersions, setShowVersions] = useState(false);
  const [allVersions, setAllVersions] = useState({ current: [], history: [] });
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  
  // Usecase Summary state
  const [showUsecaseSummary, setShowUsecaseSummary] = useState(false);
  const [usecaseSummary, setUsecaseSummary] = useState('');
  const [usecaseSummaryVersion, setUsecaseSummaryVersion] = useState(0);
  const [savingUsecaseSummary, setSavingUsecaseSummary] = useState(false);
  const [generatingUsecaseSummary, setGeneratingUsecaseSummary] = useState(false);
  const [showUsecaseSummaryHistory, setShowUsecaseSummaryHistory] = useState(false);
  const [usecaseSummaryVersions, setUsecaseSummaryVersions] = useState([]);
  const [usecaseSummaryDiffData, setUsecaseSummaryDiffData] = useState(null);
  const [selectedUsecaseSummaryVersion, setSelectedUsecaseSummaryVersion] = useState(null);
  const [loadingUsecaseSummaryDiff, setLoadingUsecaseSummaryDiff] = useState(false);
  
  // Other dialogs
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [showGlossaryRegen, setShowGlossaryRegen] = useState(false);
  
  // Glossary regeneration state
  const [glossaryType, setGlossaryType] = useState('table'); // 'table' or 'column'
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]); // columns to regenerate
  const [glossaryInstructions, setGlossaryInstructions] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  
  // Add dialogs
  const [newColumn, setNewColumn] = useState({ concept_id: '', source_table: '', source_column: '' });
  const [newLink, setNewLink] = useState({ concept_id: '', parent_table_name: '', parent_column_name: '', child_table_name: '', child_column_name: '' });

  // Get unique tables from columns
  const uniqueTables = [...new Set(allColumns.map(c => c.source_table))].filter(Boolean);
  
  // Get columns for selected table
  const columnsForTable = allColumns.filter(c => c.source_table === selectedTable);

  // Load all data
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, conceptsRes] = await Promise.all([
        getAllMetadata(),
        getConcepts()
      ]);
      setAllColumns(metaRes.data.columns || []);
      setAllLinks(metaRes.data.links || []);
      setConcepts(conceptsRes.data.concepts || []);
    } catch (err) {
      toast.error('Failed to load metadata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Load all versions
  const loadAllVersions = async () => {
    setLoadingVersions(true);
    try {
      const res = await getAllMetadataVersions();
      setAllVersions(res.data);
    } catch (err) {
      toast.error('Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = async (row, field, value) => {
    try {
      await updateColumnMetadata(row.concept_id, row.id, { [field]: value });
      loadAllData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Open edit dialog
  const handleEditCell = (row, column) => {
    setEditingRow(row);
    setEditingColumn(column);
    setEditValue(row[column.key] || '');
    setShowEditDialog(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingRow || !editingColumn) return;
    setSaving(true);
    try {
      await updateColumnMetadata(editingRow.concept_id, editingRow.id, { 
        [editingColumn.key]: editValue 
      });
      toast.success('Saved successfully');
      setShowEditDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Open tags dialog
  const handleEditTags = (row) => {
    setTagsRow(row);
    setCurrentTags(row.column_tags || []);
    setNewTagInput('');
    setShowTagsDialog(true);
  };

  // Add tag
  const handleAddTag = () => {
    if (!newTagInput.trim()) return;
    if (currentTags.includes(newTagInput.trim())) {
      toast.error('Tag already exists');
      return;
    }
    setCurrentTags([...currentTags, newTagInput.trim()]);
    setNewTagInput('');
  };

  // Remove tag
  const handleRemoveTag = (tag) => {
    setCurrentTags(currentTags.filter(t => t !== tag));
  };

  // Save tags
  const handleSaveTags = async () => {
    if (!tagsRow) return;
    setSaving(true);
    try {
      await updateColumnMetadata(tagsRow.concept_id, tagsRow.id, { 
        column_tags: currentTags 
      });
      toast.success('Tags saved');
      setShowTagsDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to save tags');
    } finally {
      setSaving(false);
    }
  };

  // View samples
  const handleViewSamples = (row) => {
    setSamplesRow(row);
    setShowSamplesDialog(true);
  };

  // Restore version
  const handleRestoreVersion = async (conceptId, versionId) => {
    if (!window.confirm('Restore to this version? Current changes will be saved in history.')) return;
    try {
      await restoreMetadataVersion(conceptId, versionId);
      loadAllData();
      loadAllVersions();
      toast.success('Version restored');
    } catch (err) {
      toast.error('Failed to restore');
    }
  };

  // Add new column
  const handleAddColumn = async () => {
    if (!newColumn.concept_id || !newColumn.source_table || !newColumn.source_column) {
      toast.error('All fields are required');
      return;
    }
    try {
      await addColumnMetadata(newColumn.concept_id, {
        source_table: newColumn.source_table,
        source_column: newColumn.source_column
      });
      setShowAddColumn(false);
      setNewColumn({ concept_id: '', source_table: '', source_column: '' });
      loadAllData();
      toast.success('Column added');
    } catch (err) {
      toast.error('Failed to add column');
    }
  };

  // Add new link
  const handleAddLink = async () => {
    if (!newLink.concept_id || !newLink.parent_table_name || !newLink.parent_column_name || !newLink.child_table_name || !newLink.child_column_name) {
      toast.error('All fields are required');
      return;
    }
    try {
      await addLink(newLink.concept_id, {
        parent_table_name: newLink.parent_table_name,
        parent_column_name: newLink.parent_column_name,
        child_table_name: newLink.child_table_name,
        child_column_name: newLink.child_column_name
      });
      setShowAddLink(false);
      setNewLink({ concept_id: '', parent_table_name: '', parent_column_name: '', child_table_name: '', child_column_name: '' });
      loadAllData();
      toast.success('Link added');
    } catch (err) {
      toast.error('Failed to add link');
    }
  };

  // Initialize glossary regen dialog
  const openGlossaryRegen = () => {
    setGlossaryType('table');
    setSelectedTable('');
    setSelectedColumns([]);
    setGlossaryInstructions('');
    setShowGlossaryRegen(true);
  };

  // When table is selected for column glossary, select all columns by default
  useEffect(() => {
    if (glossaryType === 'column' && selectedTable) {
      setSelectedColumns(columnsForTable.map(c => c.id));
    }
  }, [glossaryType, selectedTable, columnsForTable]);

  // Regenerate glossary
  const handleRegenerateGlossary = async () => {
    if (!selectedTable) {
      toast.error('Please select a table');
      return;
    }
    
    // Find the concept_id for this table
    const tableColumn = allColumns.find(c => c.source_table === selectedTable);
    if (!tableColumn) {
      toast.error('Could not find concept for table');
      return;
    }
    
    setRegenerating(true);
    try {
      const res = await regenerateGlossary(tableColumn.concept_id, {
        special_instructions: glossaryInstructions,
        target: glossaryType,
        column_ids: glossaryType === 'column' ? selectedColumns : undefined
      });
      if (res.data.success) {
        toast.success(res.data.message);
        loadAllData();
        setShowGlossaryRegen(false);
      } else {
        toast.error(res.data.message || 'Failed to regenerate');
      }
    } catch (err) {
      toast.error('Failed to regenerate glossary');
    } finally {
      setRegenerating(false);
    }
  };

  // Usecase Summary functions
  const loadUsecaseSummary = async () => {
    try {
      const res = await getUsecaseSummary();
      setUsecaseSummary(res.data.content || '');
      setUsecaseSummaryVersion(res.data.version || 0);
    } catch (err) {
      toast.error('Failed to load usecase summary');
    }
  };

  const handleGenerateUsecaseSummary = async () => {
    setGeneratingUsecaseSummary(true);
    try {
      const res = await generateUsecaseSummary();
      setUsecaseSummary(res.data.content || '');
      toast.success('Usecase summary generated from metadata');
    } catch (err) {
      toast.error('Failed to generate usecase summary');
    } finally {
      setGeneratingUsecaseSummary(false);
    }
  };

  const saveUsecaseSummaryContent = async () => {
    setSavingUsecaseSummary(true);
    try {
      await updateUsecaseSummary({ content: usecaseSummary });
      toast.success('Usecase summary saved');
      loadUsecaseSummary();
      loadUsecaseSummaryVersionsData();
    } catch (err) {
      toast.error('Failed to save usecase summary');
    } finally {
      setSavingUsecaseSummary(false);
    }
  };

  const loadUsecaseSummaryVersionsData = async () => {
    try {
      const res = await getUsecaseSummaryVersions();
      setUsecaseSummaryVersions(res.data || []);
    } catch (err) {
      toast.error('Failed to load usecase summary versions');
    }
  };

  const loadUsecaseSummaryDiffData = async (versionNum) => {
    setLoadingUsecaseSummaryDiff(true);
    setSelectedUsecaseSummaryVersion(versionNum);
    try {
      const res = await getUsecaseSummaryDiff(versionNum);
      setUsecaseSummaryDiffData(res.data);
    } catch (err) {
      toast.error('Failed to load diff');
      setUsecaseSummaryDiffData(null);
    } finally {
      setLoadingUsecaseSummaryDiff(false);
    }
  };

  const restoreUsecaseSummaryVersionData = async (versionNum) => {
    try {
      await restoreUsecaseSummaryVersion(versionNum);
      toast.success('Version restored');
      loadUsecaseSummary();
      loadUsecaseSummaryVersionsData();
    } catch (err) {
      toast.error('Failed to restore version');
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#030712]" data-testid="metadata-page">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] flex items-center justify-between bg-[#0F172A]">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-semibold text-[#F1F5F9] flex items-center gap-2">
              <Database className="w-5 h-5 text-[#0EA5E9]" />
              Metadata Management
            </h1>
            <p className="text-sm text-[#64748B]">Excel-like view for all column metadata and table relationships</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadUsecaseSummary(); setShowUsecaseSummary(true); }}
            className="border-[#1E293B] text-[#0EA5E9]"
            data-testid="usecase-summary-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            Usecase Summary
          </Button>
          <Badge variant="outline" className="text-xs">
            {allColumns.length} columns · {allLinks.length} links
          </Badge>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sub-header */}
          <div className="p-3 border-b border-[#1E293B] flex items-center justify-between bg-[#0F172A]/50">
            <div className="flex items-center gap-3">
              <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
                <TabsList className="bg-[#030712]">
                  <TabsTrigger value="columns" className="text-xs data-[state=active]:bg-[#0EA5E9]/20">
                    <Columns className="w-3 h-3 mr-1" />
                    Column Metadata ({allColumns.length})
                  </TabsTrigger>
                  <TabsTrigger value="links" className="text-xs data-[state=active]:bg-[#0EA5E9]/20">
                    <LinkIcon className="w-3 h-3 mr-1" />
                    Link Identification ({allLinks.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => {
                  loadAllVersions();
                  setShowVersions(true);
                }}
              >
                <History className="w-4 h-4 mr-1" />
                Versions
              </Button>
              <Button size="sm" variant="ghost" onClick={openGlossaryRegen}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Regenerate Glossary
              </Button>
              {activeSubTab === 'columns' && (
                <Button size="sm" onClick={() => setShowAddColumn(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Column
                </Button>
              )}
              {activeSubTab === 'links' && (
                <Button size="sm" onClick={() => setShowAddLink(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Link
                </Button>
              )}
            </div>
          </div>

          {/* Table Content */}
          <div className="flex-1 p-4 overflow-hidden">
            {activeSubTab === 'columns' && (
              <ExcelTable
                columns={COLUMN_METADATA_COLUMNS}
                data={allColumns}
                onToggle={handleToggleChange}
                onEditCell={handleEditCell}
                onViewSamples={handleViewSamples}
                onEditTags={handleEditTags}
              />
            )}

            {activeSubTab === 'links' && (
              <ExcelTable
                columns={LINK_COLUMNS}
                data={allLinks}
                onToggle={() => {}}
                onEditCell={() => {}}
                onViewSamples={() => {}}
                onEditTags={() => {}}
              />
            )}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#0EA5E9]" />
              Edit {editingColumn?.label}
            </DialogTitle>
          </DialogHeader>
          {editingRow && editingColumn && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Table</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{editingRow.source_table}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Column</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{editingRow.source_column}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{editingColumn.label}</Label>
                {editingColumn.editGroup === 'glossary' ? (
                  <Textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder={`Enter ${editingColumn.label.toLowerCase()}...`}
                    className="bg-[#030712] border-[#1E293B] min-h-[120px]"
                  />
                ) : (
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder={`Enter ${editingColumn.label.toLowerCase()}...`}
                    className="bg-[#030712] border-[#1E293B]"
                  />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#0EA5E9]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tags Edit Dialog */}
      <Dialog open={showTagsDialog} onOpenChange={setShowTagsDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#0EA5E9]" />
              Edit Column Tags
            </DialogTitle>
          </DialogHeader>
          {tagsRow && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Table</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{tagsRow.source_table}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Column</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{tagsRow.source_column}</span>
                </div>
              </div>
              
              {/* Current tags */}
              <div className="space-y-2">
                <Label>Current Tags</Label>
                <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-[#030712] rounded-md border border-[#1E293B]">
                  {currentTags.length === 0 ? (
                    <span className="text-[#64748B] text-sm italic">No tags</span>
                  ) : (
                    currentTags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1 pr-1">
                        {tag}
                        <button 
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:bg-red-500/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>
              
              {/* Add new tag */}
              <div className="space-y-2">
                <Label>Add New Tag</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    placeholder="Enter tag name..."
                    className="bg-[#030712] border-[#1E293B]"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <Button onClick={handleAddTag} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagsDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTags} disabled={saving} className="bg-[#0EA5E9]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sample Values Dialog */}
      <Dialog open={showSamplesDialog} onOpenChange={setShowSamplesDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#0EA5E9]" />
              Sample Values
            </DialogTitle>
          </DialogHeader>
          {samplesRow && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Table</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{samplesRow.source_table}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Column</Badge>
                  <span className="text-sm font-mono text-[#0EA5E9]">{samplesRow.source_column}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Sample Values ({samplesRow.sample_values?.length || 0})</Label>
                <div className="max-h-[200px] overflow-y-auto p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-1">
                  {(samplesRow.sample_values || []).length === 0 ? (
                    <span className="text-[#64748B] text-sm italic">No sample values available</span>
                  ) : (
                    samplesRow.sample_values.map((val, i) => (
                      <div key={i} className="font-mono text-sm text-[#F1F5F9] py-1 px-2 bg-[#1E293B]/50 rounded">
                        {val}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSamplesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              All Metadata Version History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {loadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
              </div>
            ) : (
              <>
                {/* Current Versions */}
                <div className="space-y-2">
                  <Label className="text-sm text-[#94A3B8]">Current Versions</Label>
                  {allVersions.current?.map((meta) => (
                    <div key={meta.concept_id} className="p-3 rounded-md border bg-[#0EA5E9]/10 border-[#0EA5E9]/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">{meta.concept_name}</Badge>
                          <Badge variant="outline">v{meta.version}</Badge>
                          <span className="text-xs text-[#64748B]">
                            {meta.columns?.length || 0} columns, {meta.links?.length || 0} links
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Historical Versions */}
                <div className="space-y-2">
                  <Label className="text-sm text-[#94A3B8]">History</Label>
                  {allVersions.history?.length === 0 ? (
                    <p className="text-center text-[#64748B] py-4">No version history</p>
                  ) : (
                    allVersions.history?.map((version) => (
                      <div key={version.id} className="p-3 rounded-md border bg-[#030712] border-[#1E293B]">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-[10px]">{version.concept_name}</Badge>
                            <Badge variant="outline">v{version.version}</Badge>
                            <span className="text-xs text-[#64748B]">
                              {new Date(version.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setExpandedVersion(expandedVersion === version.id ? null : version.id)}
                            >
                              {expandedVersion === version.id ? 'Hide Diff' : 'Show Diff'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRestoreVersion(version.concept_id, version.id)}>
                              <RotateCcw className="w-3 h-3 mr-1" /> Restore
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-[#94A3B8]">{version.change_summary}</p>
                        
                        {/* Diff section */}
                        {expandedVersion === version.id && version.diff && (
                          <div className="mt-3 p-3 bg-[#0F172A] rounded border border-[#1E293B] space-y-3">
                            {/* Columns Added */}
                            {version.diff.columns_added?.length > 0 && (
                              <div>
                                <span className="text-xs text-green-400 font-medium">+ Columns Added:</span>
                                <div className="mt-1 space-y-1">
                                  {version.diff.columns_added.map((col, i) => (
                                    <div key={i} className="text-xs text-green-300 font-mono bg-green-500/10 px-2 py-1 rounded">
                                      {col.source_table}.{col.source_column}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Columns Removed */}
                            {version.diff.columns_removed?.length > 0 && (
                              <div>
                                <span className="text-xs text-red-400 font-medium">- Columns Removed:</span>
                                <div className="mt-1 space-y-1">
                                  {version.diff.columns_removed.map((col, i) => (
                                    <div key={i} className="text-xs text-red-300 font-mono bg-red-500/10 px-2 py-1 rounded">
                                      {col.source_table}.{col.source_column}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Columns Modified */}
                            {version.diff.columns_modified?.length > 0 && (
                              <div>
                                <span className="text-xs text-yellow-400 font-medium">~ Columns Modified:</span>
                                <div className="mt-1 space-y-2">
                                  {version.diff.columns_modified.map((mod, i) => (
                                    <div key={i} className="bg-yellow-500/10 px-2 py-1 rounded">
                                      <div className="text-xs text-yellow-300 font-mono mb-1">{mod.column}</div>
                                      {mod.changes.map((change, j) => (
                                        <div key={j} className="text-[10px] text-[#94A3B8] ml-2">
                                          <span className="text-[#64748B]">{change.field}:</span>{' '}
                                          <span className="text-red-300 line-through">{JSON.stringify(change.old_value)}</span>
                                          {' → '}
                                          <span className="text-green-300">{JSON.stringify(change.new_value)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Links Added/Removed */}
                            {version.diff.links_added?.length > 0 && (
                              <div>
                                <span className="text-xs text-green-400 font-medium">+ Links Added:</span>
                                <div className="mt-1">
                                  {version.diff.links_added.map((link, i) => (
                                    <div key={i} className="text-xs text-green-300 font-mono">
                                      {link.parent_table_name}.{link.parent_column_name} → {link.child_table_name}.{link.child_column_name}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {version.diff.links_removed?.length > 0 && (
                              <div>
                                <span className="text-xs text-red-400 font-medium">- Links Removed:</span>
                                <div className="mt-1">
                                  {version.diff.links_removed.map((link, i) => (
                                    <div key={i} className="text-xs text-red-300 font-mono">
                                      {link.parent_table_name}.{link.parent_column_name} → {link.child_table_name}.{link.child_column_name}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* No changes */}
                            {!version.diff.columns_added?.length && 
                             !version.diff.columns_removed?.length && 
                             !version.diff.columns_modified?.length &&
                             !version.diff.links_added?.length &&
                             !version.diff.links_removed?.length && (
                              <p className="text-xs text-[#64748B] italic">No differences detected</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Column Dialog */}
      <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">Add Column Metadata</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Concept *</Label>
              <Select value={newColumn.concept_id} onValueChange={(v) => setNewColumn(prev => ({ ...prev, concept_id: v }))}>
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue placeholder="Select concept" />
                </SelectTrigger>
                <SelectContent>
                  {concepts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Source Table *</Label>
              <Input
                value={newColumn.source_table}
                onChange={(e) => setNewColumn(prev => ({ ...prev, source_table: e.target.value }))}
                placeholder="e.g., customers"
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>
            <div className="space-y-2">
              <Label>Source Column *</Label>
              <Input
                value={newColumn.source_column}
                onChange={(e) => setNewColumn(prev => ({ ...prev, source_column: e.target.value }))}
                placeholder="e.g., customer_id"
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddColumn(false)}>Cancel</Button>
            <Button onClick={handleAddColumn} className="bg-[#0EA5E9]">Add Column</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">Add Link Relationship</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Concept *</Label>
              <Select value={newLink.concept_id} onValueChange={(v) => setNewLink(prev => ({ ...prev, concept_id: v }))}>
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue placeholder="Select concept" />
                </SelectTrigger>
                <SelectContent>
                  {concepts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parent Table *</Label>
                <Input
                  value={newLink.parent_table_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, parent_table_name: e.target.value }))}
                  placeholder="e.g., customers"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
              <div className="space-y-2">
                <Label>Parent Column *</Label>
                <Input
                  value={newLink.parent_column_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, parent_column_name: e.target.value }))}
                  placeholder="e.g., customer_id"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
            </div>
            <div className="flex justify-center">
              <ArrowRight className="w-6 h-6 text-[#64748B]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Child Table *</Label>
                <Input
                  value={newLink.child_table_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, child_table_name: e.target.value }))}
                  placeholder="e.g., orders"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
              <div className="space-y-2">
                <Label>Child Column *</Label>
                <Input
                  value={newLink.child_column_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, child_column_name: e.target.value }))}
                  placeholder="e.g., customer_id"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)}>Cancel</Button>
            <Button onClick={handleAddLink} className="bg-[#0EA5E9]">Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Glossary Dialog */}
      <Dialog open={showGlossaryRegen} onOpenChange={setShowGlossaryRegen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#0EA5E9]" />
              Regenerate Glossary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Glossary Type Selection */}
            <div className="space-y-2">
              <Label>Glossary Type *</Label>
              <Select value={glossaryType} onValueChange={(v) => { setGlossaryType(v); setSelectedTable(''); setSelectedColumns([]); }}>
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="table">Table Glossary</SelectItem>
                  <SelectItem value="column">Column Glossary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table Selection */}
            <div className="space-y-2">
              <Label>Select Table *</Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue placeholder="Select a table" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueTables.map(table => (
                    <SelectItem key={table} value={table}>{table}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Column Selection (only for column glossary) */}
            {glossaryType === 'column' && selectedTable && (
              <div className="space-y-2">
                <Label>Select Columns to Regenerate</Label>
                <div className="max-h-[200px] overflow-y-auto p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                  {columnsForTable.map((col) => (
                    <div key={col.id} className="flex items-center gap-2">
                      <Checkbox
                        id={col.id}
                        checked={selectedColumns.includes(col.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedColumns([...selectedColumns, col.id]);
                          } else {
                            setSelectedColumns(selectedColumns.filter(id => id !== col.id));
                          }
                        }}
                      />
                      <label htmlFor={col.id} className="text-sm text-[#F1F5F9] font-mono cursor-pointer">
                        {col.source_column}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-[#64748B]">
                  <span>{selectedColumns.length} of {columnsForTable.length} selected</span>
                  <button 
                    className="text-[#0EA5E9] hover:underline"
                    onClick={() => setSelectedColumns(columnsForTable.map(c => c.id))}
                  >
                    Select All
                  </button>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="space-y-2">
              <Label>Special Instructions (Optional)</Label>
              <Textarea
                value={glossaryInstructions}
                onChange={(e) => setGlossaryInstructions(e.target.value)}
                placeholder="E.g., Focus on business context, use simple language, include data quality notes..."
                className="bg-[#030712] border-[#1E293B] min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGlossaryRegen(false)}>Cancel</Button>
            <Button 
              onClick={handleRegenerateGlossary} 
              disabled={regenerating || !selectedTable || (glossaryType === 'column' && selectedColumns.length === 0)} 
              className="bg-[#0EA5E9]"
            >
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usecase Summary Dialog */}
      <Dialog open={showUsecaseSummary} onOpenChange={setShowUsecaseSummary}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0EA5E9]" />
              Usecase Summary
              {usecaseSummaryVersion > 0 && (
                <Badge variant="outline" className="text-xs ml-2">v{usecaseSummaryVersion}</Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Auto-generate or manually write a summary of the use case based on your metadata.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col h-[55vh]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm text-[#94A3B8]">Summary Content</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { loadUsecaseSummaryVersionsData(); setShowUsecaseSummaryHistory(true); }}
                  className="text-[#64748B]"
                >
                  <History className="w-4 h-4 mr-1" />
                  History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateUsecaseSummary}
                  disabled={generatingUsecaseSummary}
                  className="border-[#0EA5E9] text-[#0EA5E9]"
                >
                  {generatingUsecaseSummary ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  Auto-Generate
                </Button>
              </div>
            </div>
            <div className="flex-1 border border-[#1E293B] rounded-md overflow-hidden">
              <CodeMirror
                value={usecaseSummary}
                onChange={(value) => setUsecaseSummary(value)}
                theme={oneDark}
                extensions={[markdown()]}
                className="h-full text-sm"
                placeholder={`# Usecase Summary\n\n## Overview\nDescribe the overall use case...\n\n## Data Sources\n- Table 1\n- Table 2\n\n## Key Metrics\n- Metric 1`}
              />
            </div>
            <div className="mt-2 p-2 bg-[#030712] rounded border border-[#1E293B]">
              <p className="text-[10px] text-[#64748B]">
                <strong>Tip:</strong> Use <code className="text-[#0EA5E9]">Auto-Generate</code> to create a summary from your table metadata, then edit as needed.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsecaseSummary(false)}>Cancel</Button>
            <Button onClick={saveUsecaseSummaryContent} disabled={savingUsecaseSummary} className="bg-[#0EA5E9]">
              {savingUsecaseSummary ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Summary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usecase Summary Version History Dialog */}
      <Dialog open={showUsecaseSummaryHistory} onOpenChange={(open) => { setShowUsecaseSummaryHistory(open); if (!open) { setSelectedUsecaseSummaryVersion(null); setUsecaseSummaryDiffData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              Usecase Summary - Version History
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-4 h-[60vh]">
            <div className="w-64 shrink-0 overflow-y-auto pr-2 border-r border-[#1E293B]">
              <div className="space-y-2">
                {usecaseSummaryVersions.length === 0 ? (
                  <p className="text-center text-[#64748B] py-8">No version history</p>
                ) : (
                  usecaseSummaryVersions.map((version, index) => (
                    <div
                      key={version.id}
                      className={cn(
                        "p-3 rounded-md border cursor-pointer transition-colors",
                        selectedUsecaseSummaryVersion === version.version
                          ? "bg-[#0EA5E9]/20 border-[#0EA5E9]/50"
                          : index === 0
                            ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/15"
                            : "bg-[#030712] border-[#1E293B] hover:bg-[#1E293B]/50"
                      )}
                      onClick={() => loadUsecaseSummaryDiffData(version.version)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-xs">v{version.version}</Badge>
                        {index === 0 && <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Current</Badge>}
                      </div>
                      <p className="text-[10px] text-[#64748B] mt-1">
                        {new Date(version.created_at).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-[#94A3B8] mt-1 truncate">{version.change_note}</p>
                      {index > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-2 h-7 text-xs w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreUsecaseSummaryVersionData(version.version);
                          }}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingUsecaseSummaryDiff ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
                </div>
              ) : usecaseSummaryDiffData ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[#F1F5F9] flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Content Changes
                  </h4>
                  <div className="rounded-md overflow-hidden border border-[#1E293B]">
                    <ReactDiffViewer
                      oldValue={usecaseSummaryDiffData.content?.old || ''}
                      newValue={usecaseSummaryDiffData.content?.new || ''}
                      splitView={false}
                      useDarkTheme={true}
                      compareMethod={DiffMethod.LINES}
                      styles={{
                        variables: {
                          dark: {
                            diffViewerBackground: '#030712',
                            addedBackground: '#10B98133',
                            removedBackground: '#EF444433',
                            addedColor: '#10B981',
                            removedColor: '#EF4444',
                            wordAddedBackground: '#10B98155',
                            wordRemovedBackground: '#EF444455',
                          }
                        },
                        contentText: { fontSize: '12px', fontFamily: 'monospace' }
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-[#64748B]">
                  <div className="text-center">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a version to view changes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
